<template>
  <div class="invoice-filters card border-0 shadow-sm mb-4">
    <div class="card-body">
      <div class="filter-row">
        <div class="count-block">
          <span class="count-num">{{ resultCount }}</span>
          <span class="count-lbl">results</span>
        </div>

        <div class="filter-search">
          <label
            class="form-label text-uppercase small text-muted fw-semibold"
            for="invoice-project-search"
          >Search invoices</label>
          <div class="input-group">
            <span class="input-group-text bg-white border-end-0">
              <i class="si si-magnifier text-muted" />
            </span>
            <input
              id="invoice-project-search"
              v-model="search"
              type="search"
              class="form-control border-start-0"
              :class="{ 'border-active': search !== '' }"
              placeholder="Project, expert name, or email"
              @input="scheduleFilterChange"
            >
          </div>
        </div>

        <div class="filter-company">
          <label class="form-label text-uppercase small text-muted fw-semibold">Company</label>
          <company-multiselect
            ref="companyFilter"
            :companies-url="companiesUrl"
            @input="onCompanyChange"
          />
        </div>

        <div
          v-if="showStatusFilter"
          class="filter-status"
        >
          <label
            class="form-label text-uppercase small text-muted fw-semibold"
            for="invoice-status-filter"
          >Status</label>
          <select
            id="invoice-status-filter"
            v-model="invoiceStatus"
            class="form-select"
            :class="{ 'border-active': invoiceStatus !== '__all__' }"
            @change="emitFilterChange"
          >
            <option value="__all__">
              All statuses
            </option>
            <option value="unpaid">
              Unpaid
            </option>
            <option value="send_to_client">
              Sent to Client
            </option>
            <option value="paid">
              Paid
            </option>
          </select>
        </div>

        <div class="filter-date-preset">
          <label
            class="form-label text-uppercase small text-muted fw-semibold"
            for="invoice-date-preset"
          >{{ datePeriodLabel }}</label>
          <select
            id="invoice-date-preset"
            v-model="datePreset"
            class="form-select"
            :class="{ 'border-active': isDateActive }"
            @change="onDatePresetChange"
          >
            <option value="all">
              All time
            </option>
            <option value="this_month">
              This month
            </option>
            <option value="last_month">
              Last month
            </option>
            <option value="last_30_days">
              Last 30 days
            </option>
            <option value="custom">
              Custom...
            </option>
          </select>
        </div>

        <template v-if="datePreset === 'custom'">
          <div class="filter-date">
            <label
              class="form-label text-uppercase small text-muted fw-semibold"
              for="invoice-date-from"
            >From</label>
            <input
              id="invoice-date-from"
              v-model="dateFrom"
              type="date"
              class="form-control"
              :class="{ 'is-invalid': dateRangeError !== '' }"
              @change="onCustomDateChange"
            >
          </div>
          <div class="filter-date">
            <label
              class="form-label text-uppercase small text-muted fw-semibold"
              for="invoice-date-to"
            >To</label>
            <input
              id="invoice-date-to"
              v-model="dateTo"
              type="date"
              class="form-control"
              :class="{ 'is-invalid': dateRangeError !== '' }"
              @change="onCustomDateChange"
            >
          </div>
          <span
            v-if="dateRangeError"
            class="date-range-error"
          >{{ dateRangeError }}</span>
        </template>

        <div
          v-if="datePreset !== 'all' && datePreset !== 'custom'"
          class="filter-date-hint"
        >
          <span class="badge bg-light text-dark border">{{ activeDateLabel }}</span>
        </div>

        <div class="filter-actions">
          <div class="filter-clear">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              :class="{ 'btn-clear-active': hasActiveFilters }"
              :disabled="!hasActiveFilters"
              @click="clearFilters"
            >
              <i class="si si-close me-1" />
              Clear
            </button>
          </div>

          <div class="filter-show-all">
            <button
              type="button"
              class="btn btn-sm"
              :class="showAll ? 'btn-primary' : 'btn-outline-secondary'"
              @click="toggleShowAll"
            >
              <i class="fa fa-list me-1" /> {{ showAll ? 'Paginated' : 'View All' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CompanyMultiselect from "../shared/CompanyMultiselect.vue";

function formatLocalDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

export default {
    name: "InvoiceFilterBar",
    components: { CompanyMultiselect },
    props: {
        companiesUrl: {
            type: String,
            required: true,
        },
        resultCount: {
            type: Number,
            default: 0,
        },
        datePeriodLabel: {
            type: String,
            default: "Payment request date",
        },
        showStatusFilter: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            search: "",
            selectedCompany: null,
            invoiceStatus: "__all__",
            datePreset: "all",
            dateFrom: "",
            dateTo: "",
            dateRangeError: "",
            showAll: false,
            debounceTimer: null,
        };
    },
    computed: {
        hasActiveFilters() {
            return this.search !== ""
                || this.selectedCompany !== null
                || (this.showStatusFilter && this.invoiceStatus !== "__all__")
                || this.datePreset !== "all";
        },
        isDateActive() {
            return this.datePreset !== "all";
        },
        resolvedDateFrom() {
            if (this.datePreset === "all") return "";
            if (this.datePreset === "custom") return this.dateFrom;
            return this.computePresetRange().from;
        },
        resolvedDateTo() {
            if (this.datePreset === "all") return "";
            if (this.datePreset === "custom") return this.dateTo;
            return this.computePresetRange().to;
        },
        activeDateLabel() {
            if (this.datePreset === "all") return "";
            if (this.datePreset === "custom") {
                if (this.dateFrom || this.dateTo) {
                    return `${this.dateFrom || "..."} – ${this.dateTo || "..."}`;
                }
                return "";
            }
            const r = this.computePresetRange();
            return `${r.from} – ${r.to}`;
        },
    },
    beforeDestroy() {
        window.clearTimeout(this.debounceTimer);
    },
    methods: {
        onCompanyChange(company) {
            this.selectedCompany = company;
            this.emitFilterChange();
        },
        computePresetRange() {
            const now = new Date();
            const y = now.getFullYear();
            const m = now.getMonth();

            if (this.datePreset === "this_month") {
                return {
                    from: formatLocalDate(new Date(y, m, 1)),
                    to: formatLocalDate(new Date(y, m + 1, 0)),
                };
            }
            if (this.datePreset === "last_month") {
                return {
                    from: formatLocalDate(new Date(y, m - 1, 1)),
                    to: formatLocalDate(new Date(y, m, 0)),
                };
            }
            if (this.datePreset === "last_30_days") {
                const from = new Date(now);
                from.setDate(from.getDate() - 29);
                return {
                    from: formatLocalDate(from),
                    to: formatLocalDate(now),
                };
            }
            return { from: "", to: "" };
        },
        scheduleFilterChange() {
            window.clearTimeout(this.debounceTimer);
            const trimmed = this.search.trim();
            if (trimmed.length > 0 && trimmed.length <= 2) {
                return;
            }
            this.debounceTimer = window.setTimeout(() => {
                this.emitFilterChange();
            }, 300);
        },
        onDatePresetChange() {
            this.dateRangeError = "";
            if (this.datePreset === "custom") {
                return;
            }
            this.dateFrom = "";
            this.dateTo = "";
            this.emitFilterChange();
        },
        onCustomDateChange() {
            this.dateRangeError = "";
            if (this.dateFrom && this.dateTo && this.dateFrom > this.dateTo) {
                this.dateRangeError = "From must be before To";
                return;
            }
            this.emitFilterChange();
        },
        emitFilterChange() {
            this.$emit("filter-change", {
                search: this.search.trim(),
                clientId: this.selectedCompany?.id ?? "",
                invoiceStatus: this.invoiceStatus,
                datePreset: this.datePreset,
                dateFrom: this.resolvedDateFrom,
                dateTo: this.resolvedDateTo,
                showAll: this.showAll,
            });
        },
        toggleShowAll() {
            this.showAll = !this.showAll;
            this.emitFilterChange();
        },
        clearFilters() {
            this.search = "";
            this.selectedCompany = null;
            if (this.$refs.companyFilter) {
                this.$refs.companyFilter.reset();
            }
            this.invoiceStatus = "__all__";
            this.datePreset = "all";
            this.dateFrom = "";
            this.dateTo = "";
            this.dateRangeError = "";
            this.showAll = false;
            this.emitFilterChange();
        },
    },
};
</script>

<style scoped>
.invoice-filters {
    border-radius: 12px;
}

.filter-row {
    display: flex;
    gap: 14px;
    align-items: center;
    flex-wrap: wrap;
}

.filter-search {
    flex: 1;
    min-width: 180px;
}

.filter-company {
    flex: 0 0 190px;
    min-width: 140px;
}

.filter-status {
    flex: 0 0 170px;
    min-width: 130px;
}

.filter-date-preset {
    flex: 0 0 160px;
    min-width: 130px;
}

.filter-date {
    flex: 0 0 150px;
    min-width: 120px;
}

.filter-date-hint {
    flex-shrink: 0;
    padding-top: 26px;
}

.filter-date-hint .badge {
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.02em;
}

.date-range-error {
    color: #dc3545;
    font-size: 0.78rem;
    padding-top: 28px;
    white-space: nowrap;
}

.filter-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-shrink: 0;
    padding-top: 24px;
}

.filter-clear,
.filter-show-all {
    flex-shrink: 0;
}

.count-block {
    border-right: 3px solid #153c4a;
    padding-right: 18px;
    flex-shrink: 0;
}

.count-num {
    display: block;
    font-size: 1.875rem;
    font-weight: 700;
    color: #153c4a;
    line-height: 1;
}

.count-lbl {
    display: block;
    font-size: 0.625rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6c757d;
    margin-top: 2px;
}

.border-active {
    border-color: #153c4a !important;
}

.btn-clear-active {
    border-color: #dc3545 !important;
    color: #dc3545 !important;
}
</style>
